// CoordCLOManagement.jsx
// Coordinator can do everything admin does — but all writes go through requests
import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, PencilSquareIcon,
  LinkIcon, CheckCircleIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const DOMAINS  = ['cognitive','affective','psychomotor'];
const BLOOMS   = ['remember','understand','apply','analyze','evaluate','create'];
const BLOOMS_COLOR = { remember:'#6b7194', understand:'#3b6ef8', apply:'#10c98e', analyze:'#f59e0b', evaluate:'#ef4444', create:'#8b5cf6' };
const DOMAIN_COLOR = { cognitive:'#3b6ef8', affective:'#10c98e', psychomotor:'#f59e0b' };
const curYear  = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ccm * { box-sizing:border-box; }
  .ccm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ccmFade .4s ease both; }
  @keyframes ccmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ccm-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ccm-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .ccm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:10px 14px; font-size:.76rem; color:#c2410c; margin-bottom:1.2rem; display:flex; align-items:center; gap:8px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-orange { background:#f97316; color:white; } .btn-orange:hover { background:#ea6c0a; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-teal   { background:#0d9488; color:white; } .btn-teal:hover { background:#0f766e; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.4rem; width:fit-content; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .filters { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-input,.fl-select { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:130px; }
  .fl-input:focus,.fl-select:focus { border-color:#f97316; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .clo-table { width:100%; border-collapse:collapse; }
  .clo-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .clo-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .clo-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .clo-table tbody tr:last-child { border-bottom:none; }
  .clo-table tbody tr:hover { background:#fafbff; }
  .clo-table td { padding:11px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .clo-code { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:700; color:#3b6ef8; background:#eef2ff; padding:3px 9px; border-radius:6px; display:inline-block; }
  .pill { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 9px; border-radius:99px; white-space:nowrap; text-transform:capitalize; }
  .plo-badge { font-family:'JetBrains Mono',monospace; font-size:.68rem; font-weight:700; padding:2px 8px; border-radius:6px; background:#f3f0ff; color:#8b5cf6; }
  .ccm-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ccm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* Map tab */
  .clo-map-row { display:flex; align-items:center; justify-content:space-between; padding:11px 14px; border-bottom:1px solid #f3f5fb; gap:10px; flex-wrap:wrap; }
  .clo-map-row:last-child { border-bottom:none; }
  .clo-map-info { flex:1; min-width:0; }
  .clo-map-code { font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:700; color:#3b6ef8; }
  .clo-map-desc { font-size:.76rem; color:#1a1d2e; margin-top:2px; }
  .clo-map-meta { font-size:.68rem; color:#9094a8; margin-top:2px; }
  .map-sel { font-family:'Sora',sans-serif; font-size:.8rem; padding:7px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:160px; cursor:pointer; }
  .map-sel:focus { border-color:#8b5cf6; }
  .map-sel.mapped { border-color:#8b5cf6; background:#f3f0ff; color:#8b5cf6; }
  /* Modal */
  .ccm-backdrop { position:fixed; top:0; left:0; width:100vw; height:100vh; background:rgba(15,17,35,.65); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; z-index:99999; overflow:hidden; }
  .ccm-modal { background:white; border-radius:20px; padding:26px; width:520px; max-width:calc(100vw - 40px); max-height:85vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.3); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .mf { display:flex; flex-direction:column; gap:5px; }
  .mf.full { grid-column:1/-1; }
  .mf-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .mf-input,.mf-select,.mf-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .mf-input:focus,.mf-select:focus,.mf-textarea:focus { border-color:#f97316; background:white; }
  .mf-textarea { resize:vertical; min-height:52px; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; }
  .req-note { background:#fff7ed; border:1px solid #fed7aa; border-radius:8px; padding:8px 12px; font-size:.74rem; color:#c2410c; margin-top:10px; }
  /* Mobile cards for CLO table */
  .ccm-cards { display:none; padding:10px; }
  .ccm-mcard { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .ccm-mc-top { display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; margin-bottom:8px; }
  .ccm-mc-desc { font-size:.78rem; color:#1a1d2e; line-height:1.45; margin-bottom:10px; }
  .ccm-mc-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .ccm-mc-row:last-of-type { border-bottom:none; }
  .ccm-mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .ccm-mc-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:768px) {
    .ccm-title { font-size:1.1rem; }
    .filters { flex-direction:column; }
    .fl-input,.fl-select { width:100%; min-width:unset; }
    .modal-grid { grid-template-columns:1fr; }
    .mf.full { grid-column:1; }
    .ccm-modal { padding:18px; }
    .modal-btns .btn { flex:1; justify-content:center; }
    .ccm-tbl-wrap { display:none !important; }
    .ccm-cards { display:block; }
  }
`;

export default function CoordCLOManagement() {
  const [tab,          setTab]         = useState('clos');
  const [classes,      setClasses]     = useState([]);
  const [plos,         setPlos]        = useState([]);
  const [clos,         setClos]        = useState([]);
  const [loading,      setLoading]     = useState(false);
  const [filterClass,  setFilterClass] = useState('');
  const [filterSubject,setFilterSubject]= useState('');
  const [filterYear,   setFilterYear]  = useState(curYear());
  const [modal,        setModal]       = useState(null); // null | 'add' | clo obj (edit)
  const [form,         setForm]        = useState({});
  const [saving,       setSaving]      = useState(false);
  const [cloMappings,  setCloMappings] = useState({});
  const [savingMap,    setSavingMap]   = useState(false);

  useEffect(() => {
    api.get('/coordinator/classes').then(r => setClasses(r.data||[])).catch(()=>{});
    api.get('/coordinator/plos').then(r => setPlos(r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    if (tab === 'clos') fetchCLOs();
    if (tab === 'map')  fetchCLOs();
  }, [tab, filterClass, filterSubject, filterYear]);

  const fetchCLOs = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterClass)   p.append('classId',     filterClass);
      if (filterSubject) p.append('subject',      filterSubject);
      if (filterYear)    p.append('academicYear', filterYear);
      const { data } = await api.get(`/coordinator/clos?${p}`);
      setClos(Array.isArray(data) ? data : []);
      // Init map state
      const m = {};
      (Array.isArray(data)?data:[]).forEach(c => { m[c._id] = c.plo?._id||c.plo||''; });
      setCloMappings(m);
    } catch { toast.error('Failed to load CLOs'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (modal) { document.documentElement.style.overflow='hidden'; document.body.style.overflow='hidden'; }
    else       { document.documentElement.style.overflow=''; document.body.style.overflow=''; }
    return () => { document.documentElement.style.overflow=''; document.body.style.overflow=''; };
  }, [modal]);

  const openAdd = () => {
    setForm({ classId: filterClass||'', subject: filterSubject||'', academicYear: filterYear, section:'', code:'', description:'', domain:'cognitive', bloomsLevel:'understand', threshold:'' });
    setModal('add');
  };

  const openEdit = (clo) => {
    setForm({ cloId: clo._id, cloCode: clo.code, description: clo.description, domain: clo.domain, bloomsLevel: clo.bloomsLevel, threshold: clo.threshold??'' });
    setModal('edit');
  };

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'add') {
        if (!form.classId||!form.subject||!form.code||!form.description)
          return toast.error('Class, subject, code and description are required');
        await api.post('/coordinator/request/clo-create', {
          classId: form.classId, subject: form.subject, academicYear: form.academicYear,
          section: form.section||'', code: form.code, description: form.description,
          domain: form.domain, bloomsLevel: form.bloomsLevel, threshold: form.threshold||null,
        });
        toast.success('CLO creation request submitted');
      } else if (modal === 'edit') {
        if (!form.description) return toast.error('Description is required');
        await api.post('/coordinator/request/clo-edit', {
          cloId: form.cloId, cloCode: form.cloCode,
          description: form.description, domain: form.domain,
          bloomsLevel: form.bloomsLevel, threshold: form.threshold||null,
        });
        toast.success('CLO edit request submitted');
      }
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSaveMap = async () => {
    setSavingMap(true);
    try {
      const mappings = Object.entries(cloMappings).map(([cloId, ploId]) => ({ cloId, ploId: ploId||null }));
      await api.post('/coordinator/request/clo-map-plo', { mappings });
      toast.success('CLO → PLO mapping request submitted');
    } catch { toast.error('Failed'); }
    finally { setSavingMap(false); }
  };

  const subjects = filterClass
    ? [...new Set((classes.find(c=>String(c._id)===String(filterClass))?.subjects||[]).flatMap(s=>s.split(',').map(x=>x.trim())).filter(Boolean))].sort()
    : [...new Set(clos.map(c=>c.subject).filter(Boolean))].sort();

  return (
    <div className="ccm"><style>{css}</style>
      <div className="ccm-hdr">
        <div>
          <div className="ccm-title">CLO Management</div>
          <div className="ccm-sub">Manage Course Learning Outcomes — all changes go through admin approval</div>
        </div>
        {tab==='clos' && <button className="btn btn-orange" onClick={openAdd}><PlusIcon/>Request New CLO</button>}
        {tab==='map'  && <button className="btn btn-teal" onClick={handleSaveMap} disabled={savingMap}><PaperAirplaneIcon/>{savingMap?'Submitting…':'Submit Mapping Request'}</button>}
      </div>

      <div className="req-banner">
        📋 All changes (create, edit, delete, mapping) are submitted as requests — admin must approve before they take effect.
      </div>

      <div className="tab-row">
        <button className={`tab-btn ${tab==='clos'?'active':''}`} onClick={()=>setTab('clos')}>CLOs</button>
        <button className={`tab-btn ${tab==='map'?'active':''}`}  onClick={()=>setTab('map')}><LinkIcon style={{width:12,height:12,display:'inline',marginRight:4}}/>Map CLOs → PLOs</button>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="fl">
          <label className="fl-label">Class</label>
          <select className="fl-select" value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSubject('');}}>
            <option value="">All Classes</option>
            {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="fl">
          <label className="fl-label">Subject</label>
          {filterClass
            ? <select className="fl-select" value={filterSubject} onChange={e=>setFilterSubject(e.target.value)}><option value="">All Subjects</option>{sortAsc(subjects).map(s=><option key={s} value={s}>{s}</option>)}</select>
            : <input className="fl-input" placeholder="Type subject…" value={filterSubject} onChange={e=>setFilterSubject(e.target.value)}/>}
        </div>
        <div className="fl">
          <label className="fl-label">Academic Year</label>
          <input className="fl-input" value={filterYear} onChange={e=>setFilterYear(e.target.value)} placeholder="2025-2026" style={{minWidth:110}}/>
        </div>
      </div>

      {/* CLOs tab */}
      {tab==='clos' && (
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">CLOs ({clos.length})</div>
          </div>
          {loading ? <div className="ccm-loading"><div className="spinner"/>Loading…</div>
          : clos.length===0 ? <div className="ccm-empty">No CLOs found. Use filters or request a new CLO.</div>
          : <>
            <div className="ccm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="clo-table">
                <thead><tr>
                  <th>Code</th><th>Description</th><th>Subject</th>
                  <th>Domain</th><th>Bloom's</th><th>PLO</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {clos.map(clo => (
                    <tr key={clo._id}>
                      <td><span className="clo-code">{clo.code}</span></td>
                      <td style={{maxWidth:220,fontSize:'.78rem'}}>{clo.description}</td>
                      <td style={{fontSize:'.78rem'}}>{clo.subject}</td>
                      <td><span className="pill" style={{background:`${DOMAIN_COLOR[clo.domain]}18`,color:DOMAIN_COLOR[clo.domain]}}>{clo.domain}</span></td>
                      <td><span className="pill" style={{background:`${BLOOMS_COLOR[clo.bloomsLevel]}18`,color:BLOOMS_COLOR[clo.bloomsLevel]}}>{clo.bloomsLevel}</span></td>
                      <td>{clo.plo ? <span className="plo-badge">{clo.plo?.code||'Mapped'}</span> : <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>}</td>
                      <td>
                        <div style={{display:'flex',gap:5}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(clo)}><PencilSquareIcon/>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ccm-cards">
              {clos.map(clo => (
                <div key={clo._id} className="ccm-mcard">
                  <div className="ccm-mc-top">
                    <span className="clo-code">{clo.code}</span>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                      <span className="pill" style={{background:`${DOMAIN_COLOR[clo.domain]}18`,color:DOMAIN_COLOR[clo.domain]}}>{clo.domain}</span>
                      <span className="pill" style={{background:`${BLOOMS_COLOR[clo.bloomsLevel]}18`,color:BLOOMS_COLOR[clo.bloomsLevel]}}>{clo.bloomsLevel}</span>
                    </div>
                  </div>
                  <div className="ccm-mc-desc">{clo.description}</div>
                  <div className="ccm-mc-row"><span className="ccm-mc-label">Subject</span><span style={{fontWeight:600}}>{clo.subject}</span></div>
                  <div className="ccm-mc-row">
                    <span className="ccm-mc-label">PLO</span>
                    {clo.plo ? <span className="plo-badge">{clo.plo?.code||'Mapped'}</span> : <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>}
                  </div>
                  <div className="ccm-mc-actions">
                    <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(clo)}><PencilSquareIcon/>Edit</button>
                  </div>
                </div>
              ))}
            </div>
          </>}
        </div>
      )}

      {/* Map CLOs → PLOs tab */}
      {tab==='map' && (
        <>
          <div style={{background:'#f0f4ff',border:'1px solid #c7d2fe',borderRadius:12,padding:'10px 14px',marginBottom:'1.2rem',fontSize:'.78rem',color:'#3730a3'}}>
            🔗 Assign each CLO to a PLO below, then click <strong>Submit Mapping Request</strong>. Admin must approve before mappings take effect.
          </div>
          <div className="panel">
            <div className="panel-head"><div className="panel-title">CLOs ({clos.length}) — assign to PLO</div></div>
            {loading ? <div className="ccm-loading"><div className="spinner"/>Loading…</div>
            : clos.length===0 ? <div className="ccm-empty">No CLOs found. Apply filters.</div>
            : clos.map(clo => (
              <div key={clo._id} className="clo-map-row">
                <div className="clo-map-info">
                  <span className="clo-map-code">{clo.code}</span>
                  <div className="clo-map-desc">{clo.description}</div>
                  <div className="clo-map-meta">{clo.class?.name} · {clo.subject} · {clo.academicYear}</div>
                </div>
                <select
                  className={`map-sel${cloMappings[clo._id]?' mapped':''}`}
                  value={cloMappings[clo._id]||''}
                  onChange={e=>setCloMappings(p=>({...p,[clo._id]:e.target.value}))}>
                  <option value="">— No PLO —</option>
                  {plos.map(p=><option key={p._id} value={p._id}>{p.code} — {p.description?.slice(0,35)}{p.description?.length>35?'…':''}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add / Edit Modal */}
      {modal && createPortal(
        <div className="ccm-backdrop" onClick={()=>setModal(null)}>
          <div className="ccm-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{modal==='add'?'Request New CLO':'Request CLO Edit'}</div>
              <button className="modal-close" onClick={()=>setModal(null)}><XMarkIcon/></button>
            </div>
            <div className="modal-grid">
              {modal==='add' && (<>
                <div className="mf">
                  <label className="mf-label">Class *</label>
                  <select className="mf-select" value={form.classId||''} onChange={e=>setF('classId',e.target.value)}>
                    <option value="">Select…</option>
                    {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="mf">
                  <label className="mf-label">Subject *</label>
                  <input className="mf-input" placeholder="e.g. Mathematics" value={form.subject||''} onChange={e=>setF('subject',e.target.value)}/>
                </div>
                <div className="mf">
                  <label className="mf-label">Academic Year</label>
                  <input className="mf-input" value={form.academicYear||''} onChange={e=>setF('academicYear',e.target.value)}/>
                </div>
                <div className="mf">
                  <label className="mf-label">Section <span style={{fontWeight:400,color:'#b0b5cc'}}>(blank = all)</span></label>
                  <input className="mf-input" placeholder="e.g. A" value={form.section||''} onChange={e=>setF('section',e.target.value)}/>
                </div>
                <div className="mf">
                  <label className="mf-label">CLO Code *</label>
                  <input className="mf-input" placeholder="e.g. CLO-1" value={form.code||''} onChange={e=>setF('code',e.target.value)} style={{fontFamily:"'JetBrains Mono',monospace"}}/>
                </div>
              </>)}

              {modal==='edit' && (
                <div className="mf">
                  <label className="mf-label">Editing</label>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.8rem',color:'#8b5cf6',fontWeight:700,padding:'9px 11px',background:'#f3f0ff',borderRadius:9}}>{form.cloCode}</div>
                </div>
              )}

              <div className="mf full">
                <label className="mf-label">Description *</label>
                <textarea className="mf-textarea" placeholder="Students will be able to…" value={form.description||''} onChange={e=>setF('description',e.target.value)}/>
              </div>
              <div className="mf">
                <label className="mf-label">Domain</label>
                <select className="mf-select" value={form.domain||'cognitive'} onChange={e=>setF('domain',e.target.value)}>
                  {DOMAINS.map(d=><option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
                </select>
              </div>
              <div className="mf">
                <label className="mf-label">Bloom's Level</label>
                <select className="mf-select" value={form.bloomsLevel||'understand'} onChange={e=>setF('bloomsLevel',e.target.value)}>
                  {BLOOMS.map(b=><option key={b} value={b}>{b.charAt(0).toUpperCase()+b.slice(1)}</option>)}
                </select>
              </div>
              <div className="mf">
                <label className="mf-label">Threshold % <span style={{fontWeight:400,color:'#b0b5cc',fontSize:'.64rem'}}>(blank = default)</span></label>
                <input type="number" min={0} max={100} className="mf-input" placeholder="e.g. 60" value={form.threshold||''} onChange={e=>setF('threshold',e.target.value)} style={{fontFamily:"'JetBrains Mono',monospace"}}/>
              </div>
            </div>
            <div className="req-note">📋 This will be submitted as a request — admin must approve before it takes effect.</div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleSave} disabled={saving}><PaperAirplaneIcon/>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}